
import React, { useState, useEffect } from 'react';
import { ArrowUpRight, ArrowDownRight, Check, Zap, ChevronDown, ChevronRight, Target, X, ShieldAlert } from 'lucide-react';

export const Badge = ({ children, className = "" }: { children?: React.ReactNode; className?: string; key?: React.Key }) => (
  <span className={`px-2 py-0.5 md:px-3 md:py-1 rounded-full text-[7px] md:text-[9px] font-black uppercase tracking-widest border transition-all inline-flex items-center justify-center whitespace-nowrap ${className}`}>
    {children}
  </span>
);

export const ToastNotification = ({ title, message, type, onClose }: any) => {
  useEffect(() => {
    const timer = setTimeout(onClose, 8000);
    return () => clearTimeout(timer);
  }, [onClose]);

  const styles = {
    error: 'bg-red-600 border-red-500 text-white shadow-red-900/20',
    success: 'bg-emerald-600 border-emerald-500 text-white shadow-emerald-900/20',
    warning: 'bg-amber-500 border-amber-400 text-white shadow-amber-900/20',
    info: 'bg-primary border-primary text-white shadow-primary/20'
  };

  return (
    <div className={`fixed top-6 left-1/2 -translate-x-1/2 z-[3000] w-full max-w-sm p-5 rounded-[28px] border-2 shadow-2xl animate-in slide-in-from-top-10 duration-500 flex items-start gap-4 ${styles[type as keyof typeof styles]}`}>
      <div className="p-2.5 bg-white/20 rounded-xl shrink-0">
        {type === 'error' ? <ShieldAlert size={20} /> : <Zap size={20} />}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-[10px] font-black uppercase tracking-[0.2em] opacity-60 mb-1">{title}</p>
        <p className="text-xs font-bold leading-relaxed">{message}</p>
      </div>
      <button onClick={onClose} className="p-1 hover:bg-white/10 rounded-lg transition-colors"><X size={18} /></button>
    </div>
  );
};

export const Card = ({ children, className = "", onClick }: { children?: React.ReactNode; className?: string; onClick?: () => void; key?: React.Key }) => (
  <div 
    onClick={onClick} 
    className={`rounded-[24px] md:rounded-[32px] border border-gray-100 shadow-sm overflow-hidden transition-all ${onClick ? 'cursor-pointer hover:shadow-xl hover:border-primary/20' : ''} ${className}`}
  >
    {children}
  </div>
);

export const StatCard = ({ title, value, icon: Icon, colorClass, subtitle, trend }: any) => (
  <Card className="p-5 md:p-6 lg:p-8 flex flex-col gap-2 md:gap-4 group bg-white h-full">
    <div className="flex justify-between items-start">
      <div className={`w-10 h-10 md:w-14 md:h-14 flex items-center justify-center rounded-[14px] md:rounded-[20px] transition-all group-hover:scale-110 shadow-sm shrink-0 ${colorClass}`}>
        <Icon className="w-5 h-5 md:w-6 md:h-6" strokeWidth={2.5} />
      </div>
      {trend && (
        <div className={`flex items-center gap-1 text-[8px] md:text-[10px] font-black uppercase tracking-widest ${trend.positive ? 'text-green-600' : 'text-red-600'}`}>
          {trend.positive ? <ArrowUpRight size={12} /> : <ArrowDownRight size={12} />}
          {trend.value}
        </div>
      )}
    </div>
    <div className="mt-1">
      <span className="text-xl md:text-2xl lg:text-3xl font-black text-secondary tracking-tighter block truncate">{value}</span>
      <h3 className="text-[7px] md:text-[9px] lg:text-[10px] font-black text-gray-400 uppercase tracking-[0.15em] mt-0.5 md:mt-1 truncate">{title}</h3>
      {subtitle && <p className="text-[8px] md:text-[9px] text-gray-400 mt-1 font-medium truncate">{subtitle}</p>}
    </div>
  </Card>
);

export const CircularGoalGauge = ({ percentage, targetValue, currentValue, label }: any) => (
  <Card className="p-6 md:p-8 bg-white border-2 border-gray-100 h-full flex flex-col shadow-lg min-w-[280px]">
    <div className="flex justify-between items-start mb-4">
       <div>
          <h3 className="text-[10px] md:text-[11px] font-black text-primary uppercase tracking-widest leading-tight">{label}</h3>
          <p className="text-[7px] md:text-[8px] text-gray-400 font-bold uppercase tracking-widest mt-0.5">(Faturamento)</p>
       </div>
       <div className="p-2 bg-gray-50 rounded-lg text-gray-400"><Target size={14} /></div>
    </div>

    <div className="flex-1 flex flex-col items-center justify-center relative py-4">
       <div className="relative w-32 h-32 md:w-40 md:h-40 lg:w-44 lg:h-44 flex items-center justify-center">
          <svg className="w-full h-full transform -rotate-90 absolute inset-0" viewBox="0 0 100 100">
             <circle 
              cx="50" cy="50" r="42" 
              stroke="currentColor" strokeWidth="6" 
              fill="transparent" 
              className="text-gray-100" 
             />
             <circle 
              cx="50" cy="50" r="42" 
              stroke="#00a8e0" strokeWidth="6" 
              fill="transparent" 
              className="transition-all duration-1000 ease-out" 
              strokeDasharray={`${percentage * 2.63}, 263.8`} 
              strokeLinecap="round"
             />
          </svg>
          <div className="flex flex-col items-center justify-center z-10">
             <span className="text-3xl md:text-4xl lg:text-5xl font-black text-secondary tracking-tighter">{percentage}%</span>
          </div>
       </div>
    </div>

    <div className="space-y-3 pt-6 mt-4 border-t border-gray-50">
       <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-1">
          <span className="text-[8px] md:text-[9px] font-black text-gray-400 uppercase tracking-widest">Valor da Meta</span>
          <Badge className="bg-slate-100 text-secondary border-none text-[8px] md:text-[9px] py-1 px-3 font-black self-start">R$ {targetValue.toLocaleString('pt-BR')}</Badge>
       </div>
       <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-1">
          <span className="text-[8px] md:text-[9px] font-black text-gray-400 uppercase tracking-widest">Alcançado</span>
          <Badge className="bg-primary/10 text-primary border-none text-[8px] md:text-[9px] py-1 px-3 font-black self-start">R$ {currentValue.toLocaleString('pt-BR')}</Badge>
       </div>
    </div>
  </Card>
);

export const TabButton = ({ children, active, onClick }: any) => (
  <button 
    onClick={onClick} 
    className={`px-4 md:px-6 py-3 md:py-4 text-[9px] md:text-[10px] font-black uppercase tracking-widest border-b-4 transition-all whitespace-nowrap ${
      active 
        ? 'border-primary text-primary bg-primary/5' 
        : 'border-transparent text-gray-400 hover:text-secondary hover:bg-gray-50'
    }`}
  >
    {children}
  </button>
);

export const InfoItem = ({ label, value, icon: Icon, className = "" }: any) => (
  <div className={className}>
    <p className="text-[8px] md:text-[9px] text-gray-400 font-black uppercase tracking-widest mb-1.5">{label}</p>
    <div className="flex items-center gap-2 text-xs md:text-sm font-bold text-secondary">
      {Icon && <Icon size={14} className="text-primary flex-shrink-0" />}
      <span className="truncate">{value || '---'}</span>
    </div>
  </div>
);

export const SidebarItem = ({ active, icon: Icon, label, onClick, isSubItem = false, isCollapsed = false, isHighlight = false }: any) => (
  <button 
    onClick={onClick}
    title={isCollapsed ? label : undefined}
    className={`w-full flex items-center transition-all text-left group overflow-hidden ${
      isCollapsed ? 'justify-center px-0 py-4' : 'gap-4 px-4 md:px-5 py-3 md:py-3.5'
    } rounded-[16px] md:rounded-[18px] text-[9px] md:text-[10px] font-black uppercase tracking-widest ${
      active 
        ? (isHighlight ? 'bg-primary text-white shadow-xl shadow-primary/40 scale-[1.02]' : 'bg-white/10 text-white scale-[1.02] translate-x-1') 
        : 'text-white/40 hover:text-white hover:bg-white/5'
    } ${!isCollapsed && isSubItem ? 'pl-10 md:pl-12 py-2.5 md:py-3 opacity-80' : ''}`}
  >
    <div className="flex-shrink-0 flex items-center justify-center">
      {Icon && <Icon size={isCollapsed ? 20 : (isSubItem ? 14 : 16)} className={`transition-all ${active ? 'scale-110' : 'group-hover:scale-110'}`} />}
    </div>
    {!isCollapsed && (
      <span className="whitespace-nowrap truncate leading-none transition-colors">
        {label}
      </span>
    )}
  </button>
);

export const SidebarDropdown = ({ label, icon: Icon, children, isOpen, onToggle, isCollapsed = false }: any) => {
  return (
    <div className="space-y-1">
      <button 
        onClick={onToggle}
        title={isCollapsed ? label : undefined}
        className={`w-full flex items-center transition-all text-left ${
          isCollapsed ? 'justify-center px-0 py-4' : 'justify-between px-4 md:px-5 py-3 md:py-4'
        } rounded-[16px] md:rounded-[20px] text-[9px] md:text-[10px] font-black uppercase tracking-widest ${
          isOpen && !isCollapsed ? 'text-white bg-white/10' : 'text-white/40 hover:text-white hover:bg-white/10'
        }`}
      >
        <div className={`flex items-center ${isCollapsed ? 'justify-center' : 'gap-4'}`}>
          {Icon && <Icon size={isCollapsed ? 20 : 18} className="md:w-5 md:h-5" />}
          {!isCollapsed && <span>{label}</span>}
        </div>
        {!isCollapsed && (isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />)}
      </button>
      {isOpen && !isCollapsed && (
        <div className="animate-in slide-in-from-top-2 duration-300 space-y-1 bg-black/5 rounded-2xl mx-1 pb-2">
          {children}
        </div>
      )}
    </div>
  );
};

export const OrderTimeline = ({ currentStatus }: { currentStatus: string }) => {
  const steps = [
    { id: 'PENDENTE', label: 'Pendente', icon: '○' },
    { id: 'CONFIRMADO', label: 'Confirmado', icon: '✓' },
    { id: 'EM_CARREGAMENTO', label: 'Carga', icon: '📦' },
    { id: 'EM_ROTA', label: 'Em Rota', icon: '🚚' },
    { id: 'MONTAGEM', label: 'Montagem', icon: '🛠' },
    { id: 'CONCLUIDO', label: 'Finalizado', icon: '🏁' }
  ];

  const currentIndex = steps.findIndex(s => s.id === currentStatus);

  return (
    <div className="w-full py-4 md:py-8">
      <div className="flex items-center justify-between relative px-1 md:px-2">
        <div className="absolute top-[12px] md:top-[16px] left-6 right-6 md:left-8 md:right-8 h-[2px] bg-gray-100 z-0"></div>
        <div 
          className="absolute top-[12px] md:top-[16px] left-6 md:left-8 h-[2px] bg-gradient-to-r from-primary/50 to-primary z-0 transition-all duration-700 ease-out"
          style={{ width: `calc(${(currentIndex / (steps.length - 1)) * 100}% - 24px)` }}
        >
           <div className="absolute right-0 top-1/2 -translate-y-1/2 w-3 h-3 md:w-4 md:h-4 bg-primary/20 rounded-full animate-ping"></div>
        </div>
        {steps.map((step, index) => {
          const isCompleted = index < currentIndex;
          const isCurrent = index === currentIndex;
          return (
            <div key={step.id} className="relative z-10 flex flex-col items-center gap-2 md:gap-3">
              <div className={`w-6 h-6 md:w-8 md:h-8 rounded-full flex items-center justify-center transition-all duration-500 border-2 ${
                isCompleted 
                  ? 'bg-primary border-primary text-white scale-90' 
                  : isCurrent 
                    ? 'bg-white border-primary text-primary shadow-[0_0_10px_rgba(0,168,224,0.4)] animate-pulse scale-105 border-2 md:border-4' 
                    : 'bg-white border-gray-100 text-gray-300'
              }`}>
                {isCompleted ? <Check size={12} strokeWidth={3} /> : <span className="text-[8px] md:text-[10px] font-black">{step.icon}</span>}
              </div>
              <span className={`text-[6px] md:text-[8px] font-black uppercase tracking-widest transition-colors duration-500 text-center ${
                isCurrent ? 'text-primary' : isCompleted ? 'text-secondary' : 'text-gray-300'
              } ${isCurrent ? 'opacity-100' : 'opacity-40 hidden sm:block'}`}>
                {step.label}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
};
